import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from './useIntersectionObserver';
import Carousel from './Carousel';
import SEO from './SEO';

const AnimatedSection = ({ children }) => {
  const [ref, inView] = useIntersectionObserver({ threshold: 0.08 });
  return (
    <div ref={ref} className={`animated-section ${inView ? 'fade-in' : ''}`}>
      {children}
    </div>
  );
};

const VideoExperience = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const videoRef = useRef(null);

  const videos = [
    "/videos/nouria0.mp4",
    "/videos/nouria1.mp4",
    "/videos/nouria2.mp4",
    "/videos/nouria3.mp4"
  ];

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(err => {
        console.log("Autoplay block or interruption: ", err);
      });
    }
  }, [activeIdx]);

  return (
    <section className="video-background-section">
      <div className="video-background-overlay" />
      <video
        ref={videoRef}
        src={videos[activeIdx]}
        muted
        playsInline
        autoPlay
        onEnded={() => setActiveIdx((prev) => (prev + 1) % videos.length)}
        className="background-video"
      />
      <div className="video-text-overlay">
        <div className="container">
          <blockquote className="philosophy-quote" style={{ borderLeft: 'none', padding: 0, margin: 0 }}>
            <p style={{ color: '#FAF9F6', textShadow: '0 2px 15px rgba(0,0,0,0.7)', fontSize: 'clamp(1.3rem, 3.5vw, 2.2rem)', fontWeight: '300', lineHeight: '1.6', maxWidth: '850px', margin: '0 auto' }}>
              Health is not a destination you reach — it is a garden you tend.{"\n"}
              Every mindful meal, every fasting window, every quiet morning{"\n"}
              is a seed. Watch your world flourish. 🌿
            </p>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

function Home() {
  const faqData = [
    {
      question: "What makes Nouria different?",
      answer: "Nouria moves beyond simple calorie counting. We focus on Gut Health, Fiber Density, and Visual Gamification. It's the best choice for those who want a holistic, stress-free approach to weight loss."
    },
    {
      question: "Does it work with global cuisines?",
      answer: "Yes! Nouria is the ultimate Global Food Tracker. We recognize varied cuisines from all over the world, so you don't have to guess calories for your favorite dishes."
    }
  ];

  return (
    <main className="home-page" role="main">
      <SEO
        title="AI Gut Health & Fasting Tracker | Nouria"
        description="Your AI-powered sanctuary for sustainable gut health. Master intermittent fasting and intuitive nutrition with a companion that learns from you."
        canonical="/"
        faqData={faqData}
      />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="hero">
        <AnimatedSection>
          <div className="container" style={{ padding: '4rem 2rem' }}>
            <div className="hero-content-wrapper text-left-layout">
              <div className="hero-text-content">
                <span className="hero-eyebrow">AI · FITNESS  · GUT HEALTH · INTERMITTENT FASTING</span>
                <h1 className="hero-title">
                  Your AI-Powered Sanctuary for{' '}
                  <span className="highlight">Sustainable Gut Health.</span>
                </h1>
                <p className="hero-subtitle">
                  Master your intermittent fasting and intuitive nutrition with a companion
                  that learns from you — quietly, beautifully, without the noise.
                </p>
                <div className="hero-cta">
                  <div className="hero-cta-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.masalachailabs.nouria"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button button-primary"
                      id="hero-begin-journey-btn"
                      aria-label="Begin your gut health journey with Nouria on Google Play"
                    >
                      Begin Your Journey
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.masalachailabs.nouria"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="play-store-badge-link"
                      aria-label="Get Nouria on Google Play Store"
                    >
                      <img
                        src="/google-play-badge-en.webp"
                        alt="Get it on Google Play"
                        className="play-store-badge-img"
                      />
                    </a>
                  </div>
                  <div className="trust-signals">
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: '400' }}>
                      No calorie obsession &nbsp;·&nbsp; Photo + voice logging &nbsp;·&nbsp; Any cuisine
                    </span>
                  </div>
                </div>

                <div className="social-proof-strip">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--secondary-color)', fontSize: '1.1rem' }}>★★★★★</span>
                    <span style={{ fontWeight: '500', fontSize: '0.9rem' }}>4.9 / 5 Average Rating</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>🌿</span>
                    <span style={{ fontWeight: '500', fontSize: '0.9rem' }}>Join 10,000+ members</span>
                  </div>
                </div>
              </div>

              <div className="hero-visual">
                <div className="phone-mockup-wrapper breathing-pulse">
                  <div className="phone-mockup">
                    <img
                      src="/screen2.jpg"
                      alt="Nouria gut health dashboard"
                      fetchPriority="high"
                      className="demo-image"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── REALITY CHECK ────────────────────────────────── */}
      <section className="reality-check-section">
        <AnimatedSection>
          <div className="container">
            <div className="reality-title-wrapper">
              <span className="reality-eyebrow">Brutal facts. No sugar coating.</span>
              <h2 className="reality-title">The Middle-Age Health Trap</h2>
              <p className="reality-subtitle">If you're over 35, your body has changed. The old rules don't apply anymore. Stop guessing why you're struggling.</p>
            </div>

            <div className="reality-grid">
              <div className="reality-card">
                <span className="icon" role="img" aria-label="Scale down">📉</span>
                <h3>"I'm eating less, but the scale isn't moving. Why?"</h3>
                <p>The Metabolic Strike: Your body has gone into 'survival mode'. The old 'calories in, calories out' formula is failing you.</p>
              </div>
              <div className="reality-card">
                <span className="icon" role="img" aria-label="T-shirt">👕</span>
                <h3>"My waistline is expanding, even with salads. Why?"</h3>
                <p>The Cortisol Creep: It's not just food. Stress and hormonal shifts are packing fat exactly where you don't want it.</p>
              </div>
              <div className="reality-card">
                <span className="icon" role="img" aria-label="Hourglass">⌛</span>
                <h3>"I'm exhausted, but I'm staring at the ceiling at 3 AM. Why?"</h3>
                <p>The Sleep Paradox: You're 'tired but wired'. Your circadian rhythm is broken, and your recovery is at zero.</p>
              </div>
              <div className="reality-card">
                <span className="icon" role="img" aria-label="Battery low">🪫</span>
                <h3>"I wake up tired and stay tired all day. Why?"</h3>
                <p>The Energy Leak: Your mitochondria are struggling. You're running on fumes and caffeine, and it's catching up.</p>
              </div>
              <div className="reality-card">
                <span className="icon" role="img" aria-label="Melting face">🫠</span>
                <h3>"I'm hitting the gym, but my body is getting 'soft'. Why?"</h3>
                <p>The Muscle Melt: After 35, you lose muscle 3x faster. Without the right data, you're working hard for zero results.</p>
              </div>
              <div className="reality-card">
                <span className="icon" role="img" aria-label="Tornado">🌪️</span>
                <h3>"Anxious mornings, bloated afternoons. Why is my body at war with itself?"</h3>
                <p>The Gut-Brain Meltdown: Your gut and brain are locked in a stress loop. The bloating, the anxiety, the brain fog—it's all connected, and it's all fixable.</p>
              </div>
            </div>

            <div className="reality-footer">
              <p>You aren't broken. You're just operating without a manual.</p>
              <a
                href="https://play.google.com/store/apps/details?id=com.masalachailabs.nouria"
                target="_blank"
                rel="noopener noreferrer"
                className="button button-primary"
              >
                Stop the Guesswork — Download Now
              </a>
            </div>
          </div>
        </AnimatedSection>
      </section>
      <VideoExperience />

      {/* ── THE INNER GARDEN DASHBOARD ───────────────────── */}
      <section className="section inner-garden-section" id="inner-garden">
        <AnimatedSection>
          <div className="container">
            <span className="hero-eyebrow" style={{ textAlign: 'center', display: 'block' }}>
              FEATURE SPOTLIGHT
            </span>
            <h2 className="section-title">The Inner Garden</h2>
            <p className="section-subtitle">
              Your health, rendered as a living landscape. Every logged meal, every fasting
              window, every glass of water — reflected in a garden that grows with you.
            </p>

            <div className="inner-garden-card">
              <div className="inner-garden-grid">
                {/* Visual side */}
                <div className="inner-garden-visuals">
                  <img
                    src="/images/dashboard_anim.png"
                    alt="Nouria Inner Garden dashboard showing gut health metrics"
                    className="inner-garden-img breathing-pulse"
                    loading="lazy"
                  />
                </div>

                {/* Stats + description */}
                <div>
                  <h3 style={{ marginBottom: '0.75rem' }}>A Living Reflection of Your Inner World</h3>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: 1.8 }}>
                    As you log nutritious foods, your digital garden grows. Fiber plants flowers,
                    protein grows trees, fermented foods attract helpful critters.
                    It is not about counting — it is about watching your vitality bloom.
                  </p>

                  <div className="garden-stats">
                    <div className="stat-box">
                      <div className="stat-value">87%</div>
                      <div className="stat-label">Gut Health Score</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-value">14h</div>
                      <div className="stat-label">Fasting Window</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-value">32g</div>
                      <div className="stat-label">Fiber Today</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-value">Day 21</div>
                      <div className="stat-label">Current Streak</div>
                    </div>
                  </div>

                  <p className="garden-tagline">
                    "Nouria transformed my relationship with food. Watching my garden grow
                    makes consistency feel natural — not like a chore."
                    <br />
                    <strong style={{ color: 'var(--text-main)', fontStyle: 'normal' }}>— Emily S., Beta Member</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── AI INSIGHTS ENGINE ───────────────────────────── */}
      <section className="section ai-insights-section" id="action">
        <AnimatedSection>
          <div className="container">
            <span className="hero-eyebrow" style={{ textAlign: 'center', display: 'block' }}>
              AI COMPANION
            </span>
            <h2 className="section-title">Your AI Insights Engine</h2>
            <p className="section-subtitle">
              Log food by photo or voice. Receive gentle, personalised guidance that
              learns from your patterns — not a rigid rulebook.
            </p>

            <div className="ai-insights-card">
              {/* Feature list */}
              <div>
                <ul className="ai-feature-list">
                  {[
                    { icon: '📸', title: 'Photograph Your Plate', desc: 'Snap any meal and receive instant calorie, macro, and gut-health breakdowns in seconds.' },
                    { icon: '🎤', title: 'Voice Logging', desc: 'Speak naturally in English, Hindi, Tamil, or more. No typing, no friction.' },
                    { icon: '🌍', title: 'Global Cuisine Ready', desc: 'From biryani to burritos — our AI recognises the world\'s diversity of food.' },
                    { icon: '🏷️', title: 'Barcode & Label Scanning', desc: 'Scan packaged foods or restaurant receipts for an accurate nutritional picture.' },
                    { icon: '🔮', title: 'Personalised Gut Insights', desc: 'Identify your bloat triggers, fermented food gaps, and fibre deficiencies with gentle AI coaching.' },
                  ].map((item, i) => (
                    <li className="ai-feature-item" key={i}>
                      <span className="ai-feature-icon">{item.icon}</span>
                      <div className="ai-feature-text">
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Visual */}
              <div className="ai-insights-visuals">
                <img
                  src="/screen2.jpg"
                  alt="Nouria AI voice and meal logging dashboard"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── APP SCREENSHOTS CAROUSEL ─────────────────────── */}
      <section id="carousel" className="section" style={{ padding: '0', backgroundColor: 'transparent' }}>
        <AnimatedSection>
          <div style={{ textAlign: 'center', paddingTop: '5rem', paddingBottom: '1rem' }}>
            <span className="hero-eyebrow" style={{ display: 'block' }}>SEE IT IN ACTION</span>
            <h2 className="section-title">A Beautiful, Calm Interface</h2>
            <p className="section-subtitle">
              Explore the interface designed to make your health journey effortless and enjoyable.
            </p>
          </div>
          <Carousel />
        </AnimatedSection>
      </section>

      {/* ── SCIENCE SECTION ──────────────────────────────── */}
      <section id="science" className="section science-gradient">
        <AnimatedSection>
          <div className="container">
            <h2 className="section-title" style={{ color: '#FAF9F6' }}>The Science of You</h2>
            <p className="section-subtitle" style={{ color: '#A3B2A1' }}>
              Your gut is your "second brain." When it is healthy, everything else follows.
            </p>
            <div className="science-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', marginTop: '1rem' }}>
              <div className="science-item">
                <h3>Gut Health First</h3>
                <p style={{ color: '#C5D4C3' }}>70% of your immune system lives in your gut. Track Fibre Density and Fermented Foods to feed the beneficial microbes that protect you.</p>
              </div>
              <div className="science-item">
                <h3>Calorie Deficit & Macros</h3>
                <p style={{ color: '#C5D4C3' }}>Achieve a sustainable Calorie Deficit with our Macro Calculator. Prioritise protein and balanced nutrition for a clearer, calmer mind.</p>
              </div>
              <div className="science-item">
                <h3>Water & Smart Fasting</h3>
                <p style={{ color: '#C5D4C3' }}>Sync your eating windows with our Fasting Tracker (16:8, circadian, custom). Improve metabolic efficiency and energy throughout the day.</p>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── FAMILY PLANNER ───────────────────────────────── */}
      <section className="section family-planner-section">
        <AnimatedSection>
          <div className="container">
            <span className="hero-eyebrow" style={{ textAlign: 'center', display: 'block' }}>
              COMMUNAL WELLNESS
            </span>
            <h2 className="section-title">The Family Planner</h2>
            <p className="section-subtitle">
              Health is not a solo endeavour. Track your household's wellness together —
              privately, beautifully, and without the guesswork.
            </p>

            <div className="family-planner-grid">
              {[
                { avatar: '👩🏽', name: 'Mary', role: 'Family Head', fasting: '16:8 — Active', fiber: '28g', gut: '91%', badge: '✅ On track' },
                { avatar: '👨🏻', name: 'Robert', role: 'Partner', fasting: '14:10 — Active', fiber: '18g', gut: '72%', badge: '🌿 Improving' },
                { avatar: '👦🏽', name: 'Arthur', role: 'Child (Teen)', fasting: 'Not tracking', fiber: '12g', gut: '65%', badge: '💧 Needs water' },
                { avatar: '👵🏽', name: 'Grandma', role: 'Elder', fasting: 'Custom', fiber: '24g', gut: '84%', badge: '✅ On track' },
              ].map((m, i) => (
                <div className="family-member-card" key={i}>
                  <div className="member-header">
                    <div className="member-avatar" style={{ background: 'var(--primary-light)' }}>
                      {m.avatar}
                    </div>
                    <div className="member-info">
                      <h4>{m.name}</h4>
                      <span>{m.role}</span>
                    </div>
                  </div>
                  <div className="member-stats">
                    <div className="member-stat-row">
                      <span className="member-stat-label">Fasting</span>
                      <span className="member-stat-val">{m.fasting}</span>
                    </div>
                    <div className="member-stat-row">
                      <span className="member-stat-label">Fibre Today</span>
                      <span className="member-stat-val">{m.fiber}</span>
                    </div>
                    <div className="member-stat-row">
                      <span className="member-stat-label">Gut Score</span>
                      <span className="member-stat-val">{m.gut}</span>
                    </div>
                    <div className="member-stat-row" style={{ paddingTop: '0.75rem' }}>
                      <span className="member-status-badge">{m.badge}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </section>


      {/* ── HEALTH PROFESSIONALS ─────────────────────────── */}
      <section className="section" style={{ padding: '5rem 0', background: 'var(--surface-color)' }}>
        <AnimatedSection>
          <div className="container text-center">
            <h2 className="section-title">Are You a Health Professional?</h2>
            <p className="section-subtitle" style={{ marginBottom: '2rem' }}>
              Join our network of doctors, nutritionists, and trainers. Get free premium
              access and clinical-grade gut health insights for your clients.
            </p>
            <Link
              to="/professionals"
              className="button button-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              aria-label="Learn more about professional partnerships"
            >
              Learn More About Partnerships <span aria-hidden="true">→</span>
            </Link>
          </div>
        </AnimatedSection>
      </section>

      {/* ── SUCCESS STORIES ──────────────────────────────── */}
      <section id="stories" className="section social-proof-section" style={{ background: 'var(--background-color)' }}>
        <AnimatedSection>
          <div className="container">
            <h2 className="section-title">Stories from the Garden</h2>
            <p className="section-subtitle">Real words from people who chose a quieter path to wellness.</p>
            <div className="testimonial-grid">
              {[
                { result: '"Lost 12 lbs in 2 months"', text: 'I never realised how little fibre I was eating until I saw my garden withering. Nouria made fixing my diet a gentle game. The weight just fell off.', name: 'Emily S.', bg: '#EDE0D4' },
                { result: '"No more afternoon crash"', text: 'The fasting timer is a quiet companion. I stick to my 16:8 window effortlessly now, and my energy levels are steady all day.', name: 'Arthur K.', bg: '#D9E2D5' },
                { result: '"My bloating is gone"', text: 'The AI Gut Coach noticed I was eating too much dairy and offered gentle alternatives. Three weeks later, I feel lighter and so much calmer.', name: 'Sarah M.', bg: '#E8DDD5' },
              ].map((t, i) => (
                <div className="testimonial-card" key={i}>
                  <span className="testimonial-result">{t.result}</span>
                  <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.8 }}>{t.text}</p>
                  <div className="testimonial-author">
                    <div className="avatar" style={{ background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                    <div>
                      <strong style={{ color: 'var(--text-main)' }}>{t.name}</strong><br />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>Beta Member</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── BLOG PREVIEW ─────────────────────────────────── */}
      <section className="section blog-preview-section" style={{ background: 'var(--surface-color)' }}>
        <AnimatedSection>
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 className="section-title" style={{ margin: 0, textAlign: 'left' }}>From the Journal</h2>
              <Link to="/blogs" className="read-more" style={{ fontWeight: '500', fontSize: '1rem' }}>
                View All &rarr;
              </Link>
            </div>
            <div className="blog-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {[
                { to: '/blogs/1', title: 'The Power of Prebiotics: Feeding Your Gut Garden', summary: 'Discover the unsung heroes of gut health. Prebiotics are the fuel that keeps your good bacteria thriving.' },
                { to: '/blogs/6', title: 'Food Logging is NOT Calorie Counting', summary: 'Most people mistake food tracking to be some marketing gimmick. Wellness is a much bigger picture.' },
                { to: '/blogs/8', title: "Grocery Shopping: The 'Mundane' Mental Workout", summary: "Learn how Nouria's Grocery Guide simplifies your family's needs while navigating complex labels." },
              ].map((b, i) => (
                <Link to={b.to} className="blog-card-link" key={i}>
                  <div className="blog-card" style={{ height: '100%' }}>
                    <div className="blog-card-content">
                      <h3 className="blog-card-title">{b.title}</h3>
                      <p className="blog-card-summary">{b.summary}</p>
                      <span className="read-more">Read More &rarr;</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section id="faq" className="section faq-section" style={{ background: 'var(--background-color)' }}>
        <AnimatedSection>
          <div className="container">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="faq-container" style={{ maxWidth: '760px', margin: '0 auto' }}>
              {[
                { q: 'What makes Nouria different?', a: 'Nouria moves beyond simple calorie counting. We focus on Gut Health, Fibre Density, and the Visual Inner Garden. It\'s a holistic, stress-free approach to lasting wellness.' },
                { q: 'How does the "Inner Garden" work?', a: 'As you log nutritious foods, your digital garden grows. Fibre plants flowers, protein grows trees, and fermented foods attract helpful critters. It\'s a beautiful mirror of your internal health.' },
                { q: 'Does it work with global cuisines?', a: 'Yes! Nouria recognises varied cuisines from all over the world, so you don\'t have to guess nutritional values for your favourite dishes.' },
                { q: 'Do you offer Widgets?', a: 'Absolutely. We offer customisable Android Widgets that let you see your calorie and fasting status instantly from your home screen.' },
                { q: 'Is it suitable for Vegetarians?', a: 'Nouria is designed with plant-based preferences in mind. Our AI suggests plant-based protein sources and tracks nutrients critical for non-meat eaters.' },
              ].map((item, i) => (
                <div className="faq-item" key={i}>
                  <div className="faq-question">{item.q}</div>
                  <div className="faq-answer">{item.a}</div>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────── */}
      <AnimatedSection>
        <div className="container cta-section" style={{
          backgroundColor: 'var(--primary-color)',
          borderRadius: 'var(--radius-lg)',
          padding: '5rem 3rem',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center'
        }}>
          {/* Soft botanical watermark */}
          <div style={{
            position: 'absolute', right: '-2rem', bottom: '-2rem',
            width: '280px', height: '280px', opacity: 0.06,
            background: 'radial-gradient(circle, #FAF9F6 0%, transparent 70%)',
            borderRadius: '50%'
          }} />
          <span className="hero-eyebrow" style={{ color: 'var(--secondary-color)' }}>
            YOUR SANCTUARY AWAITS
          </span>
          <h2 style={{ color: '#FAF9F6', fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1.25rem', position: 'relative', zIndex: 1 }}>
            Ready to stop dieting and start nourishing?
          </h2>
          <p style={{ color: 'rgba(250,249,246,0.75)', marginBottom: '2.5rem', fontSize: '1.1rem', position: 'relative', zIndex: 1 }}>
            Your inner garden is waiting. Download Nouria today on Google Play.
          </p>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <a
              href="https://play.google.com/store/apps/details?id=com.masalachailabs.nouria"
              target="_blank"
              rel="noopener noreferrer"
              id="final-cta-btn"
              className="button"
              style={{
                backgroundColor: '#FAF9F6',
                color: 'var(--primary-color)',
                fontWeight: '600',
                padding: '1rem 3rem',
                fontSize: '1rem',
                letterSpacing: '0.05em'
              }}
            >
              Begin Your Journey
            </a>
          </div>
        </div>
      </AnimatedSection>
    </main>
  );
}

export default Home;